import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  RefreshCw,
  ExternalLink,
  TrendingUp,
  AlertTriangle,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import OfficeScene from './OfficeScene';

const TABS = [
  { id: 'research', label: 'Research Agent', emoji: '🔍' },
  { id: 'prd', label: 'PRD Agent', emoji: '📄' },
  { id: 'preview', label: 'Preview', emoji: '💻' },
];

const OutputPanel = ({ researchData, prdContent, sandboxUrl, isProcessing, currentPhase, selectedAgent }) => {
  const [activeTab, setActiveTab] = useState('preview');

  // Only switch to preview when the sandbox URL finally loads
  useEffect(() => {
    if (sandboxUrl) setActiveTab('preview');
  }, [sandboxUrl]);

  const hasResearch = researchData && (researchData.opportunities?.length > 0 || researchData.results?.length > 0);
  const hasPrd = !!prdContent;
  const hasPreview = !!sandboxUrl;

  return (
    <div className="flex-1 flex flex-col bg-[#F8FAFC] h-full" data-testid="output-panel">
      {/* Tab bar */}
      <div className="h-10 flex items-center gap-0.5 px-2 border-b border-[#E2E8F0] flex-shrink-0 bg-white">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const hasContent =
            (tab.id === 'research' && hasResearch) ||
            (tab.id === 'prd' && hasPrd) ||
            (tab.id === 'preview' && hasPreview);
          const isLoading =
            (tab.id === 'research' && currentPhase === 'research') ||
            (tab.id === 'prd' && currentPhase === 'prd') ||
            (tab.id === 'preview' && (currentPhase === 'design' || currentPhase === 'code'));

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`output-tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-colors ${
                isActive
                  ? 'bg-[#EFF6FF] text-[#2563EB]'
                  : 'text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
              }`}
            >
              <span className="text-xs">{tab.emoji}</span>
              {tab.label}
              {hasContent && !isActive && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" />
              )}
              {isLoading && !hasContent && (
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
              )}
            </button>
          );
        })}

        {activeTab === 'preview' && sandboxUrl && (
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => {
                const iframe = document.querySelector('[data-testid="preview-iframe"]');
                if (iframe) iframe.src = iframe.src;
              }}
              data-testid="preview-reload-button"
              className="p-1 rounded hover:bg-[#F8FAFC] text-[#94A3B8] hover:text-[#475569] transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
            <a
              href={sandboxUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="open-preview-external"
              className="p-1 rounded hover:bg-[#F8FAFC] text-[#94A3B8] hover:text-[#475569] transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>

      {/* Tab content */}
      <div className="flex-1 relative overflow-hidden">
        {/* Preview tab is always mounted, shown/hidden via visibility so iframe doesn't reload */}
        <div className={`absolute inset-0 ${activeTab === 'preview' ? 'block' : 'hidden'}`}>
          <PreviewTab
            sandboxUrl={sandboxUrl}
            isProcessing={isProcessing}
            currentPhase={currentPhase}
            selectedAgent={selectedAgent}
          />
        </div>
        {activeTab === 'research' && (
          <div className="absolute inset-0 overflow-y-auto">
            <ResearchTab data={researchData} isLoading={currentPhase === 'research'} />
          </div>
        )}
        {activeTab === 'prd' && (
          <div className="absolute inset-0 overflow-y-auto">
            <PrdTab content={prdContent} isLoading={currentPhase === 'prd'} />
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Research Tab ----
const ResearchTab = ({ data, isLoading }) => {
  if (isLoading && !data) {
    return <EmptyState emoji="🔍" text="Research Agent analyzing..." loading />;
  }
  if (!data || (!data.opportunities?.length && !data.results?.length)) {
    return <EmptyState emoji="🔍" text="Research results will appear here" />;
  }

  const opportunities = data.opportunities || [];
  const sources = data.sourcesSearched || [];
  const results = data.results || [];
  const redditResults = results.filter((r) => r.source === 'reddit');
  const perplexityResults = results.filter((r) => r.source === 'perplexity');

  return (
    <div className="p-5 space-y-6" data-testid="research-content">
      {(sources.length > 0 || results.length > 0) && (
        <div className="flex items-center gap-2 text-xs text-[#475569] pb-3 border-b border-[#E2E8F0]">
          {sources.length > 0 && <span>Sources: {sources.join(', ')}</span>}
          {data.totalSignals > 0 && <span>· {data.totalSignals} signals</span>}
          {results.length > 0 && <span>· {results.length} results</span>}
        </div>
      )}

      {opportunities.length > 0 && (
        <div className="space-y-4">
          <p className="text-[10px] font-bold text-[#475569] uppercase tracking-widest">
            Opportunities ({opportunities.length})
          </p>
          {opportunities.map((opp, i) => (
            <motion.div
              key={opp.name || i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="rounded-xl bg-white border border-[#E2E8F0] p-4 space-y-3"
              style={{ boxShadow: '0 2px 4px -2px rgba(0,0,0,0.05)' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#2563EB]">#{opp.rank}</span>
                    <h3 className="text-sm font-semibold text-[#0F172A]">{opp.name}</h3>
                    {opp.opportunitySize && (
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-[#EFF6FF] text-[#2563EB]">
                        {opp.opportunitySize === 'L' ? 'Large' : opp.opportunitySize === 'M' ? 'Medium' : 'Small'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#475569] mt-1">{opp.pitch}</p>
                </div>
                {opp.buildabilityScore && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <TrendingUp className="w-3 h-3 text-[#10B981]" />
                    <span className="text-xs font-bold text-[#10B981]">{opp.buildabilityScore}/10</span>
                  </div>
                )}
              </div>

              {opp.competitiveLandscape && (
                <div>
                  <p className="text-[10px] font-bold text-[#475569] uppercase tracking-widest mb-1">Competitive Landscape</p>
                  <p className="text-xs text-[#475569] leading-relaxed">{opp.competitiveLandscape}</p>
                </div>
              )}

              {opp.revenuePotential && (
                <div>
                  <p className="text-[10px] font-bold text-[#475569] uppercase tracking-widest mb-1">Revenue Potential</p>
                  <p className="text-xs text-[#475569]">{opp.revenuePotential}</p>
                </div>
              )}

              {opp.keyRisks?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-[#475569] uppercase tracking-widest mb-1">Key Risks</p>
                  <ul className="space-y-1">
                    {opp.keyRisks.map((risk, ri) => (
                      <li key={ri} className="flex items-start gap-1.5 text-xs text-[#475569]">
                        <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {opp.signals?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-[#475569] uppercase tracking-widest mb-1">Signals</p>
                  <div className="space-y-1">
                    {opp.signals.map((s, si) => (
                      <div key={si} className="flex items-start gap-1.5 text-xs text-[#475569]">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#EFF6FF] text-[#2563EB] font-semibold flex-shrink-0">{s.source}</span>
                        <span className="text-[#475569]">{s.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {opp.timeToMarket && (
                <div className="flex items-center gap-3 pt-1">
                  <span className="text-[10px] text-[#94A3B8]">Time to market: <span className="text-[#475569] font-semibold">{opp.timeToMarket}</span></span>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {perplexityResults.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-[#475569] uppercase tracking-widest flex items-center gap-1.5">
            <span>🔵</span> Perplexity Intelligence ({perplexityResults.length})
          </p>
          <div className="space-y-2">
            {perplexityResults.map((r, i) => (
              <div key={i} className="p-3 rounded-xl bg-white border border-[#E2E8F0] space-y-1">
                <p className="text-xs font-semibold text-[#0F172A]">{r.title}</p>
                {r.snippet && <p className="text-[11px] text-[#475569] leading-relaxed">{r.snippet}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {redditResults.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-bold text-[#475569] uppercase tracking-widest flex items-center gap-1.5">
            <span>🔴</span> Reddit Community Insights ({redditResults.length})
          </p>
          <div className="space-y-2">
            {redditResults.map((r, i) => (
              <a
                key={i}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 rounded-xl bg-white border border-[#E2E8F0] hover:border-[#2563EB]/30 hover:bg-[#EFF6FF] transition-colors"
              >
                <p className="text-xs font-semibold text-[#0F172A] line-clamp-2">{r.title}</p>
                {r.snippet && (
                  <p className="text-[11px] text-[#475569] mt-1 line-clamp-2 leading-relaxed">{r.snippet}</p>
                )}
                <p className="text-[10px] text-[#94A3B8] mt-1.5 truncate">{r.url}</p>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---- PRD Tab ----
const PrdTab = ({ content, isLoading }) => {
  if (isLoading && !content) {
    return <EmptyState emoji="📄" text="PRD Agent writing..." loading />;
  }
  if (!content) {
    return <EmptyState emoji="📄" text="PRD document will appear here" />;
  }

  return (
    <div className="p-5" data-testid="prd-content">
      <div className="prose prose-slate prose-sm max-w-none text-[#475569] [&_h1]:text-lg [&_h1]:text-[#0F172A] [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-3 [&_h2]:text-base [&_h2]:text-[#0F172A] [&_h2]:font-semibold [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:text-[#0F172A] [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5 [&_p]:text-[13px] [&_p]:leading-relaxed [&_li]:text-[13px] [&_table]:text-xs [&_th]:text-[#475569] [&_th]:px-3 [&_th]:py-1.5 [&_th]:border-[#E2E8F0] [&_td]:px-3 [&_td]:py-1.5 [&_td]:border-[#E2E8F0] [&_code]:text-[#2563EB] [&_code]:bg-[#EFF6FF] [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_pre]:bg-[#F8FAFC] [&_pre]:border [&_pre]:border-[#E2E8F0] [&_pre]:rounded-xl [&_pre]:p-3 [&_hr]:border-[#E2E8F0]">
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
    </div>
  );
};

// ---- Preview Tab ----
const PreviewTab = ({ sandboxUrl, isProcessing, currentPhase, selectedAgent }) => {
  if (sandboxUrl) {
    return (
      <iframe
        src={sandboxUrl}
        title="App Preview"
        className="w-full h-full border-0"
        data-testid="preview-iframe"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
      />
    );
  }

  return (
    <div className="w-full h-full" data-testid="preview-building-text">
      <OfficeScene currentPhase={currentPhase} selectedAgent={selectedAgent} />
    </div>
  );
};

// ---- Shared empty state ----
const EmptyState = ({ emoji, text, loading }) => (
  <div className="flex flex-col items-center justify-center h-full text-center space-y-2 px-8">
    {loading ? (
      <div className="relative w-10 h-10">
        <div className="absolute inset-0 rounded-full border border-[#2563EB]/20" />
        <div className="absolute inset-0 rounded-full border border-transparent border-t-[#2563EB]/40 animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center text-lg">{emoji}</div>
      </div>
    ) : (
      <span className="text-2xl">{emoji}</span>
    )}
    <p className="text-xs text-[#475569]">{text}</p>
  </div>
);

export default OutputPanel;
